const AD_CLICK_ID_KEYS: ReadonlySet<string> = new Set(['rdt_cid', 'twclid', 'ScCid']);

/** A page's own query params, and every key it may write. */
export interface OwnedSearchParamsOptions {
  /** The current path as the browser shows it, `/demo-mode` prefix included:
   *  `window.location.pathname` or `usePathname()` from `next/navigation`. */
  pathname: string;
  /** The page's own params, holding only non-default values. */
  params: URLSearchParams;
  /** Every key the page can write; keys outside it stay as they are. */
  ownedKeys: ReadonlySet<string>;
}

/**
 * Writes a page's own query params into the URL without a navigation and keeps
 * every param the page does not own.
 *
 * @remarks
 * Ad click IDs (`rdt_cid`, `twclid`, `ScCid`) and `utm_*` arrive in the landing
 * URL, and tracking scripts read them only after hydration, by which time a
 * page's first URL sync has already run; rebuilding the query from scratch
 * wipes them. An owned key missing from `params` is removed, so a filter reset
 * to its default leaves the URL. Nothing happens when the URL already holds the
 * same params, in any order: Next routes every history write through its
 * router, so even a reorder re-renders each `useSearchParams` consumer.
 *
 * @param options - The page's own params and the keys it owns.
 *
 * @example
 * ```ts
 * // URL: /markets?rdt_cid=abc&sort=volume
 * replaceOwnedSearchParams({
 *   pathname: '/markets',
 *   params: new URLSearchParams({ category: 'sports' }),
 *   ownedKeys: new Set(['sort', 'category']),
 * });
 * // URL: /markets?category=sports&rdt_cid=abc
 * ```
 */
export function replaceOwnedSearchParams(options: OwnedSearchParamsOptions): void {
  const target = ownedSearchParamsTarget(options);
  if (target === null) return;

  window.history.replaceState(null, '', target); // ALLOW-NETWORK-UNAWARE: silent same-origin query sync on the current path; does not navigate.
}

/**
 * Writes a page's own query params into the URL as a new history entry, so Back
 * returns to the previous params.
 *
 * @remarks
 * Keeps foreign params and skips a write that changes nothing, as
 * {@link replaceOwnedSearchParams} does.
 *
 * @param options - The page's own params and the keys it owns.
 *
 * @example
 * ```ts
 * // URL: /markets?sort=volume&category=sports
 * pushOwnedSearchParams({
 *   pathname: '/markets',
 *   params: new URLSearchParams({ category: 'crypto' }),
 *   ownedKeys: new Set(['category']),
 * });
 * // URL: /markets?category=crypto&sort=volume; Back → ?sort=volume&category=sports
 * ```
 */
export function pushOwnedSearchParams(options: OwnedSearchParamsOptions): void {
  const target = ownedSearchParamsTarget(options);
  if (target === null) return;

  window.history.pushState(null, '', target); // ALLOW-NETWORK-UNAWARE: same-origin query change on the current path; does not navigate.
}

/**
 * Returns whether a query param is ad attribution: a click ID (`rdt_cid`,
 * `twclid`, `ScCid`) or a `utm_*` campaign tag.
 *
 * @remarks
 * Ad pixels read these on the landing page. A link that opens another visit
 * (a new tab, a copied or shared URL) must not carry them, or that visit
 * replays the original ad click.
 *
 * @param key - A query param name.
 *
 * @example
 * ```ts
 * isAttributionParam('utm_source'); // true
 * isAttributionParam('sort'); // false
 * ```
 */
export function isAttributionParam(key: string): boolean {
  return AD_CLICK_ID_KEYS.has(key) || key.startsWith('utm_');
}

/** The URL to write, or `null` when the current one already holds the same params. */
function ownedSearchParamsTarget({
  pathname,
  params,
  ownedKeys,
}: OwnedSearchParamsOptions): string | null {
  const current = new URLSearchParams(window.location.search);
  const next = new URLSearchParams(params);
  for (const [key, value] of current) {
    if (!ownedKeys.has(key)) next.append(key, value);
  }

  if (pathname === window.location.pathname && sameSearchParams(next, current)) return null;

  const query = next.toString();
  return query.length > 0 ? `${pathname}?${query}` : pathname;
}

function sameSearchParams(a: URLSearchParams, b: URLSearchParams): boolean {
  const sortedA = new URLSearchParams(a);
  const sortedB = new URLSearchParams(b);
  sortedA.sort();
  sortedB.sort();
  return sortedA.toString() === sortedB.toString();
}
